// import { useIsMobile } from '@utils/useWindowSize'
import { Flex } from '@primitives/Layout'
import { ActiveLink } from '@utils/Link'
import { NavTabsHelp, SupportLink } from '@utils/NavTabs'
import { graphql, useStaticQuery } from 'gatsby'
import sortBy from 'lodash/sortBy'
import React from 'react'
import { TRANSLATED_LANGUAGE_SET } from '../../../../translation/languages'
import { NavRoot, NavSection } from './NavRoot'

// Locale of a support slug: /support/<lang>/<app>/... -> '<lang>', else null
// (English pages are bare /support/<app>/...).
const langOfSlug = slug => {
  const parts = (slug || '').split('/').filter(Boolean) // ['support', a, b, ...]
  if (parts[0] === 'support' && TRANSLATED_LANGUAGE_SET.has(parts[1]))
    return parts[1]
  return null
}

const getIdfromSlug = slug => {
  if (!slug) {
    return ''
  }

  // e.g. "/support/actions/overview/" --> 'overview'
  const parts = slug.split('/').filter(Boolean)
  return parts[parts.length - 1]
}

const useGetAllSections = (category, slug) => {
  const data = useStaticQuery(graphql`
    query navQuery {
      site {
        siteMetadata {
          docSections {
            actions {
              id
              title
            }
            timepage {
              id
              title
            }
            flow {
              id
              title
            }
            overlap {
              id
              title
            }
            account {
              id
              title
            }
            together {
              id
              title
            }
          }
        }
      }
      allMdx(filter: { fields: { slug: { glob: "/support/**" } } }) {
        nodes {
          fields {
            slug
          }
          frontmatter {
            title
            order
            section
            app
          }
        }
      }
    }
  `)
  const sections = data.site.siteMetadata.docSections
  const sectionPages = data.allMdx.nodes

  const subSections = sections?.[category?.id] ?? []
  if (!category?.id || !subSections.length) return []
  // Only show articles in the current page's locale, so translated files don't
  // pile into the English nav (and vice-versa).
  const currentLang = langOfSlug(slug)

  const getItems = section => {
    const items = sectionPages
      .filter(
        ({
          fields: { slug: pageSlug },
          frontmatter: { section: pageSection, app: pageApp }
        }) =>
          pageSection === section.id &&
          pageApp === category.id &&
          langOfSlug(pageSlug) === currentLang
      )
      .map(({ fields: { slug }, frontmatter: { title, order } }) => ({
        slug: slug,
        title: title,
        order: order,
        id: getIdfromSlug(slug)
      }))
    return sortBy(items, ['order', 'title'])
  }

  return subSections
    .map(section => ({
      id: section.id,
      title: section.title,
      items: getItems(section)
    }))
    .filter(section => section.items.length > 0)
}

const Nav = ({ category, section, slug, ...props }) => {
  const allSections = useGetAllSections(category, slug)
  // const expandable = useIsMobile()

  return (
    <Flex
      flexDirection="column"
      width="100%"
      as="nav"
      mb={[1, 2]}
      textAlign="left"
      id="nav"
      gap={4}
      {...props}
    >
      <Flex flexDirection="column" gap={3} width="100%" pt="1px">
        <SupportLink />
        <NavTabsHelp app={category.title} categoryId={category.id} />
      </Flex>

      <NavRoot
        key={`${category.id}-${section?.id ?? 'none'}`}
        multiple
        defaultValue={section?.id ? [section.id] : []}
      >
        {allSections.map(section => {
          const { id } = section
          return (
            <NavSection key={id} value={id} title={section.title}>
              {section.items.map(item => {
                const { id, title } = item
                return (
                  <ActiveLink
                    fontSize={1}
                    partiallyActive={true}
                    key={id}
                    to={item.slug}
                    title={title}
                    inline
                    lineHeight={1}
                    color="#585859"
                    gridColumn="2 / -1"
                  >
                    {title}
                  </ActiveLink>
                )
              })}
            </NavSection>
          )
        })}
      </NavRoot>
    </Flex>
  )
}

export default Nav
